import '../../../../components/reports/generic-table/generic-table.component.scss';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppTabularDto } from '../../../../components/reports/dtos/app-tabular.dto';
import { DataTableFlatComponent } from '../../../../components/reports/generic-table/data-table-flat.component';
import { CommonTableComponentProps } from '../../../../components/reports/generic-table/generic-table.component';
import { ReportsComponentProps } from '../../../../components/reports/interfaces/reports-component-props';
import {
  useDataProvider,
  useDataProviderPaginated,
} from '../../../../hooks/data-provider/data-provider.hook';
import { SortBy } from '../../../../interfaces/sort-by.interface';

interface DetailsTableWithChildrenComponentProps
  extends ReportsComponentProps,
    CommonTableComponentProps {}

export const TableInputComponent = ({
  onRowSelectionChange,
  enableRowSelection = false,
  enableMultiRowSelection,
  filterSelection,
  reportName,
  reportId,
  totalReportName,
  enabled,
  height,
  defaultSortBy,
  parseMeasuresColumns,
  parseColumns,
  startColumns,
  endColumns,
  showTotal = false,
  className,
  idField,
  setInvalidateQuery,
  enableStickyFirstColumn = false,
  suppressRowClickSelection,
}: DetailsTableWithChildrenComponentProps) => {
  const [sortBy, setSortBy] = useState<SortBy[] | undefined>(defaultSortBy);

  const {
    data: childrenData,
    isFetching,
    invalidateQuery: invalidateQueryChildren,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useDataProviderPaginated<any>(
    reportName,
    filterSelection,
    sortBy,
    enabled,
    reportId || '',
  );
  const { data: total, invalidateQuery } = useDataProvider<any>(
    totalReportName,
    filterSelection,
    undefined,
    enabled,
    true,
    reportId,
  );
  const totalData = useMemo(() => {
    if (total) {
      // Category column must not rendered. We keep category column in back-end to reuse same report for exporting.
      const columns = total.columns.filter((i) => i.id !== 'category_name');
      return { ...total, columns };
    } else {
      return total;
    }
  }, [total]);
  // Build parent data (category level) by gathering distinct categories.
  const parentData: any[] | undefined = useMemo(() => {
    if (!childrenData) {
      return undefined;
    }
    const rows = childrenData?.pages?.flatMap((page) => page.rows.concat());
    const categories: any[] = Array.from(
      new Map(
        rows.map((item) => [
          item.category_id,
          { category_id: item.category_id, category_name: item.category_name },
        ]),
      ).values(),
    );

    return categories;
  }, [childrenData]);

  // Transform paginated data into data with children.
  const data: AppTabularDto<any> | undefined = useMemo(() => {
    if (!parentData) {
      return undefined;
    }
    if (!childrenData?.pages) {
      return undefined;
    }
    // Paginated data contains rows for earch page. We flat data in rows.
    const rows = childrenData.pages?.flatMap((page) => page.rows.concat());
    // First page is out base data. This contains "columns", "measureColumns", etc.
    const page1 = childrenData.pages[0];

    const groupedRows: any[] = [];

    parentData.forEach((r) => {
      // For each parent row (category) we look for its children.
      const newRow = { ...r, name: r.category_name, id: r.category_id };
      groupedRows.push(newRow);
      newRow.children = newRow.children ?? [];
      // subRows contains children
      const subRows = rows.filter((rx: any) => {
        if (rx.category_id === newRow.category_id) {
          return true;
        } else {
          return false;
        }
      });
      newRow.children = subRows.map((i) => ({ ...i }));
    });
    // Category column must not rendered. We keep category column in back-end to reuse same report for exporting.
    const columns = page1.columns.filter((i) => i.id !== 'category_name');
    return { ...page1, columns, rows: groupedRows };
  }, [parentData, childrenData?.pages]);

  const invalidate = useCallback(async () => {
    await invalidateQuery();
    await invalidateQueryChildren();
  }, [invalidateQuery, invalidateQueryChildren]);

  useEffect(() => {
    if (setInvalidateQuery) {
      setInvalidateQuery({ invalidateQuery: invalidate });
    }
  }, [invalidate, setInvalidateQuery]);

  return (
    <DataTableFlatComponent
      data={data}
      totalData={totalData}
      isFetching={isFetching}
      parseColumns={parseColumns}
      parseMeasuresColumns={parseMeasuresColumns}
      startColumns={startColumns}
      endColumns={endColumns}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      sortBy={sortBy}
      setSortBy={setSortBy}
      className={className}
      showTotal={showTotal}
      height={height}
      idField={idField}
      enableRowSelection={enableRowSelection}
      enableMultiRowSelection={enableMultiRowSelection}
      onRowSelectionChange={onRowSelectionChange}
      enableStickyFirstColumn={enableStickyFirstColumn}
      suppressRowClickSelection={suppressRowClickSelection}
      expandRows={true}
    />
  );
};
